import type { Design } from "../content-builder/types/editor.types";
import { designFromHtml } from "../content-builder/services/htmlDesign";

export interface TemplateEditorBridge {
    loadHtml: (html: string) => void;
    getDesign: () => Design | null;
    getTemplateId?: () => number | string | null;
    getName?: () => string | null;
}

let bridge: TemplateEditorBridge | null = null;

export function registerTemplateEditorBridge(next: TemplateEditorBridge): () => void {
    bridge = next;
    return () => {
        if (bridge === next) bridge = null;
    };
}

export function getTemplateEditorBridge(): TemplateEditorBridge | null {
    return bridge;
}

export interface TemplateSaveOutcome {
    status: "saved" | "needs-name" | "failed";
    name?: string;
    templateId?: number | string | null;
    reason?: string;
    /**
     * CA-6: true when the backend rejected the generated document itself with
     * 422 `invalid_model_output`, rather than failing for an ordinary reason.
     *
     * Kept separate from `reason` because the two call for different UI: a
     * transport failure is worth retrying as-is, while invalid model output
     * needs the template generated again before any retry can succeed.
     */
    invalidModelOutput?: boolean;
}

/** Error codes the template endpoints return for an unusable generated document. */
const INVALID_MODEL_OUTPUT = "invalid_model_output";

/**
 * CA-6: "Continue rejecting or surfacing 422 invalid_model_output responses."
 *
 * The code can arrive as a top-level `code`, inside the `{error: {code}}`
 * envelope the newer services use, or only as a 422 status with the string in
 * the message — so all three are checked rather than assuming one shape.
 */
export function isInvalidModelOutput(err: any): boolean {
    const response = err?.response;
    const body = response?.data;
    const code = body?.code ?? body?.error?.code ?? body?.error_code;
    if (typeof code === "string" && code.toLowerCase() === INVALID_MODEL_OUTPUT) return true;

    if (response?.status !== 422) return false;
    const text = `${body?.message ?? body?.error?.message ?? err?.message ?? ""}`;
    return text.toLowerCase().includes(INVALID_MODEL_OUTPUT);
}

export async function runTemplateSave(params: {
    name: string | null;
    fallbackHtml?: string | null;
}): Promise<TemplateSaveOutcome> {
    const editor = getTemplateEditorBridge();

    const design: Design | null =
        editor?.getDesign?.() ??
        (params.fallbackHtml ? designFromHtml(params.fallbackHtml) : null);

    if (!design) {
        return { status: "failed", reason: "There is no generated template to save yet." };
    }

    const name = params.name?.trim() || editor?.getName?.()?.trim() || null;
    if (!name) return { status: "needs-name" };

    try {
    
        const { saveTemplateFlow, updateTemplateFlow } = await import("../content-builder/api");

        const existingId = editor?.getTemplateId?.() ?? null;
        const result = existingId
            ? await updateTemplateFlow({ id: existingId, name, design })
            : await saveTemplateFlow({ name, design });

        return { status: "saved", name, templateId: result?.id ?? existingId ?? null };
    } catch (err: any) {
        // CA-6: a 422 `invalid_model_output` is the generated document being
        // rejected, not a save that merely went wrong. Surfaced as such, so the
        // UI can ask for a regeneration instead of offering a futile retry.
        if (isInvalidModelOutput(err)) {
            return {
                status: "failed",
                name,
                invalidModelOutput: true,
                reason:
                    err?.response?.data?.message ??
                    err?.response?.data?.error?.message ??
                    "The generated template was rejected as invalid. Ask the assistant to generate it again.",
            };
        }

        return {
            status: "failed",
            name,
            reason:
                err?.response?.data?.message ??
                err?.message ??
                "The template could not be saved.",
        };
    }
}
